import { Button, Checkbox, Modal, message, Tooltip } from 'antd';
import type { FC } from 'react';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useEffect, useRef, useState } from 'react';
import TableView from '@/components/folders/TableView';
import { Documentcolumn } from '@/components/columns/DocumentsColumn';
import { DocumentView } from '@/features/documents/Sidebar/DocumentView';
import {
  CheckOutlined,
  DeleteOutlined,
  FolderOutlined,
  MinusCircleFilled,
} from '@ant-design/icons';
import {
  addAttachments,
  addInquiryFolder,
  removeAttachments,
  removeInquiryFolder,
} from '@/services/api/chatService';
import { AppConstants } from '@/utils/constants';
import { useAppContext } from '@/context';
import { useTranslation } from 'react-i18next';
import styles from '@/styles/ChatWindow.module.scss';
import { FileIconComponent } from '@/components/icons';
import useMomentWithTimezone from '@/hooks/timezone';

interface InquiryData {
  id: string;
  folders: any[];
  files: any[];
}

interface ModalAddFoldersProps {
  inquiryId: string;
  inquiry: InquiryData | any;
  setReloadInquiry: any;
  open: boolean;
  onCancel: () => void;
  reloadData: boolean;
}

const ModalAddFolders: FC<ModalAddFoldersProps> = ({
  inquiryId,
  inquiry,
  setReloadInquiry,
  open,
  onCancel,
  reloadData,
}) => {
  const { t } = useTranslation();
  const { user } = useAppContext();
  const momentWithTimezone = useMomentWithTimezone();
  const [_, setCount] = useState<number>(0);
  const [documentView] = useState(
    () => new DocumentView(user || {}, () => setCount(prev => prev + 1)),
  );
  const [currentNode, setCurrentNode] = useState<any>(null);
  const [usersInDocuments, setUsersInDocuments] = useState<any>({});
  const [selectedFolderIds, setSelectedFolderIds] = useState<string[]>([]);
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
  // Folder IDs in the current view that contain selected descendants.
  // Computed via backend on view/selection change with debounce.
  const [ancestorHintIds, setAncestorHintIds] = useState<Set<string>>(
    new Set(),
  );
  const [filesAndFolders, setFilesAndFolders] = useState<any[]>([]);
  const [inSelectedMode, setInSelectedMode] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const hintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const initFolder = () => {
      documentView.init();
      if (open) {
        setCurrentNode(null);
        documentView.backToNode(null);
        documentView.tableData = documentView.treeData.filter(
          (item: any) => !item.parent,
        );
      }
    };
    initFolder();
  }, [reloadData, open]);

  useEffect(() => {
    if (inquiry) {
      const folders = Array.isArray(inquiry.folders) ? inquiry.folders : [];
      const files = Array.isArray(inquiry.files) ? inquiry.files : [];

      const allItems = [...folders, ...files]
        .filter(Boolean)
        .filter(
          (item: any) =>
            typeof item === 'object' && item !== null && (item.id || item.name),
        );
      setFilesAndFolders(allItems);

      const selectedFolderIdsList: string[] = [];
      const selectedFileIdsList: string[] = [];

      folders.forEach((item: any) => {
        if (typeof item === 'number' || typeof item === 'string') {
          selectedFolderIdsList.push(String(item));
        } else if (item && typeof item === 'object' && item.id) {
          selectedFolderIdsList.push(String(item.id));
        }
      });

      files.forEach((item: any) => {
        if (typeof item === 'number' || typeof item === 'string') {
          selectedFileIdsList.push(String(item));
        } else if (item && typeof item === 'object' && item.id) {
          selectedFileIdsList.push(String(item.id));
        }
      });

      setSelectedFolderIds(selectedFolderIdsList);
      setSelectedFileIds(selectedFileIdsList);
    }
  }, [inquiry]);

  // Check which visible folders contain selected descendants.
  // Debounced to avoid hammering the backend on rapid selection changes.
  useEffect(() => {
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);

    hintTimerRef.current = setTimeout(() => {
      let cancelled = false;

      const check = async () => {
        const hasSelections =
          selectedFolderIds.length > 0 || selectedFileIds.length > 0;
        if (!hasSelections) {
          setAncestorHintIds(new Set());
          return;
        }

        const folders = (documentView.tableData || []).filter(
          (item: any) => !item.isLeaf && !(item.type && item.type !== ''),
        );
        if (folders.length === 0) {
          setAncestorHintIds(new Set());
          return;
        }

        const selectedFolderSet = new Set(selectedFolderIds);
        const selectedFileSet = new Set(selectedFileIds);
        const newHints = new Set<string>();

        await Promise.all(
          folders.map(async (folder: any) => {
            const folderId = String(folder.id);
            // Skip folders already in selectedFolderIds — they show as checked
            if (selectedFolderSet.has(folderId)) return;
            try {
              const desc = await documentView.getDescendantIds(folderId);
              const hasSelected =
                desc.folderIds.some(id => selectedFolderSet.has(id)) ||
                desc.fileIds.some(id => selectedFileSet.has(id));
              if (hasSelected) {
                newHints.add(folderId);
              }
            } catch {
              // ignore
            }
          }),
        );

        if (!cancelled) {
          setAncestorHintIds(newHints);
        }
      };

      check();
      return () => {
        cancelled = true;
      };
    }, 300);

    return () => {
      if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    };
  }, [_, selectedFolderIds, selectedFileIds]);

  const handleOk = async () => {
    if (loading) return;
    setLoading(true);
    const originalFolderIds: string[] = [];
    const originalFileIds: string[] = [];

    const folders = Array.isArray(inquiry?.folders) ? inquiry.folders : [];
    const files = Array.isArray(inquiry?.files) ? inquiry.files : [];

    folders.forEach((item: any) => {
      if (typeof item === 'number' || typeof item === 'string') {
        originalFolderIds.push(String(item));
      } else if (item && typeof item === 'object' && item.id) {
        originalFolderIds.push(String(item.id));
      }
    });

    files.forEach((item: any) => {
      if (typeof item === 'number' || typeof item === 'string') {
        originalFileIds.push(String(item));
      } else if (item && typeof item === 'object' && item.id) {
        originalFileIds.push(String(item.id));
      }
    });

    const validSelectedFolderIds = selectedFolderIds.filter(Boolean);
    const validSelectedFileIds = selectedFileIds.filter(Boolean);

    const folderIdsToRemove = originalFolderIds.filter(
      id => !validSelectedFolderIds.includes(String(id)),
    );
    const folderIdsToAdd = validSelectedFolderIds.filter(
      id => !originalFolderIds.includes(String(id)),
    );
    const fileIdsToRemove = originalFileIds.filter(
      id => !validSelectedFileIds.includes(String(id)),
    );
    const fileIdsToAdd = validSelectedFileIds.filter(
      id => !originalFileIds.includes(String(id)),
    );

    if (folderIdsToRemove.length > 0) {
      const { success } = await removeInquiryFolder(inquiryId, {
        folder_ids: folderIdsToRemove,
      });
      if (!success) {
        setLoading(false);
        return;
      }
    }

    if (fileIdsToRemove.length > 0) {
      const { success } = await removeAttachments(inquiryId, fileIdsToRemove);
      if (!success) {
        setLoading(false);
        return;
      }
    }

    if (folderIdsToAdd.length > 0) {
      const result = await addInquiryFolder(inquiryId, {
        folder_ids: folderIdsToAdd,
      });
      if (!result.success) {
        setLoading(false);
        return;
      }
      const responseData = result.data as any;
      if (responseData?.info) {
        message.info(t('home.chat.attachments.folders.pendingFilesInfo'), 5);
      }
    }

    if (fileIdsToAdd.length > 0) {
      try {
        const result = await addAttachments(inquiryId, {
          files: fileIdsToAdd,
        });
        if (!result.success) {
          // Don't abort
        }
      } catch (error: any) {
        console.warn('Failed to add attachments', error);
      }
    }

    setLoading(false);
    onCancel();

    setCurrentNode(null);
    setSelectedFolderIds([]);
    setSelectedFileIds([]);
    setInSelectedMode(false);

    try {
      documentView.backToNode(null);
      documentView.tableData = documentView.treeData.filter(
        (item: any) => !item.parent,
      );
    } catch (error) {
      console.warn('Error resetting documentView', error);
    }

    setReloadInquiry((prev: boolean) => !prev);
  };

  const removeFoldersAndFiles = async (attachment: any) => {
    const isFile =
      attachment.isLeaf === true || (attachment.type && attachment.type !== '');

    let success = false;
    if (isFile) {
      const result = await removeAttachments(inquiryId, {
        files: [String(attachment.id)],
      });
      success = result.success;
      if (success) {
        setSelectedFileIds(prev =>
          prev.filter(id => id !== String(attachment.id)),
        );
      }
    } else {
      const result = await removeInquiryFolder(inquiryId, {
        folder_ids: [String(attachment.id)],
      });
      success = result.success;
      if (success) {
        setSelectedFolderIds(prev =>
          prev.filter(id => id !== String(attachment.id)),
        );
      }
    }

    if (!success) {
      return;
    }
    setReloadInquiry((prev: boolean) => !prev);
  };

  /**
   * Select a folder and all its descendants via a single backend call.
   */
  const selectFolder = async (folderId: string) => {
    const descendants = await documentView.getDescendantIds(folderId);
    setSelectedFolderIds(prev => [
      ...new Set([...prev, folderId, ...descendants.folderIds]),
    ]);
    setSelectedFileIds(prev => [
      ...new Set([...prev, ...descendants.fileIds]),
    ]);
  };

  /**
   * Deselect a folder and all its descendants via a single backend call.
   */
  const deselectFolder = async (folderId: string) => {
    const descendants = await documentView.getDescendantIds(folderId);
    const folderIdsToRemove = new Set([folderId, ...descendants.folderIds]);
    const fileIdsToRemove = new Set(descendants.fileIds);
    setSelectedFolderIds(prev => prev.filter(id => !folderIdsToRemove.has(id)));
    setSelectedFileIds(prev => prev.filter(id => !fileIdsToRemove.has(id)));
  };

  const buildParentChain = (targetNode: any, treeData: any[]): any => {
    const findNodeWithPath = (
      nodeId: string,
      nodes: any[],
      path: any[] = [],
    ): { node: any; path: any[] } | null => {
      for (const node of nodes) {
        if (node.id === nodeId) {
          return { node, path };
        }
        if (node.children) {
          const result = findNodeWithPath(nodeId, node.children, [
            ...path,
            node,
          ]);
          if (result) return result;
        }
      }
      return null;
    };

    const result = findNodeWithPath(targetNode.id, treeData);
    if (!result) return null;

    const { node, path } = result;

    if (path.length === 0) {
      return { ...node, parent: null };
    }

    const buildChain = (index: number): any => {
      if (index < 0) {
        return { ...node, parent: null };
      }
      const parentNode = { ...path[index] };
      parentNode.parent = index === 0 ? null : buildChain(index - 1);
      return parentNode;
    };

    const targetNodeWithParent = { ...node };
    targetNodeWithParent.parent = buildChain(path.length - 1);

    return targetNodeWithParent;
  };

  const onLoadData = async (node: any, isBack: boolean = false) => {
    if (isBack && node === null) {
      documentView.backToNode(null);
      documentView.tableData = documentView.treeData.filter(
        (item: any) => !item.parent,
      );
      setCurrentNode(null);
      setCount(prev => prev + 1);
      return;
    }

    if (!node) return;

    const isFile = node.isLeaf === true || (node.type && node.type !== '');
    if (isFile) {
      return;
    }

    if (isBack) {
      documentView.backToNode(node);
      const nodeWithParent = buildParentChain(node, documentView.treeData);
      setCurrentNode(nodeWithParent || node);
      return;
    }

    if (node && node.children) {
      documentView.tableData = node.children;
      const nodeWithParent = buildParentChain(node, documentView.treeData);
      setCurrentNode(
        nodeWithParent || (node.isRoot ? { ...node, parent: null } : node),
      );
      setCount(prev => prev + 1);
    } else if (node && node.id !== currentNode?.id) {
      await documentView.onLoadData(node);
      const updatedNode = documentView.findNodeInTree(
        node.id,
        documentView.treeData,
      );
      const nodeWithParent = updatedNode
        ? buildParentChain(updatedNode, documentView.treeData)
        : buildParentChain(node, documentView.treeData);
      setCurrentNode(
        nodeWithParent || (node.isRoot ? { ...node, parent: null } : node),
      );
      setCount(prev => prev + 1);
    }
  };

  return (
    <div>
      <Modal
        title={t('home.chat.attachments.folders.title')}
        width={'80%'}
        open={open}
        onCancel={() => {
          setCurrentNode(null);
          setInSelectedMode(false);
          try {
            documentView.backToNode(null);
            documentView.tableData = documentView.treeData.filter(
              (item: any) => !item.parent,
            );
          } catch (error) {
            console.warn('Error resetting documentView on cancel', error);
          }
          onCancel();
        }}
        footer={
          filesAndFolders.length && !inSelectedMode ? (
            <Button onClick={() => setInSelectedMode(true)}>
              {t('home.chat.attachments.files.uploadMore')}
            </Button>
          ) : (
            <></>
          )
        }
        style={{ overflow: 'auto' }}
      >
        {filesAndFolders.length && !inSelectedMode ? (
          filesAndFolders
            .filter(
              (element: any) =>
                element != null &&
                typeof element === 'object' &&
                (element.id || element.name),
            )
            .map((element: any, index: number) => {
              if (typeof element !== 'object' || element === null) {
                return null;
              }
              const isFile =
                element?.type ||
                (element?.id && String(element.id).startsWith('file_'));
              const uniqueKey =
                element?.id ||
                `attachment-${index}-${element?.name || 'unknown'}`;

              return (
                <div key={uniqueKey} className={styles.wrapAttachments}>
                  <div className={styles.attachments}>
                    {isFile ? (
                      FileIconComponent(element?.name || 'unknown', '', 30)
                    ) : (
                      <div className={styles.filesAndFoldersAttachmentName}>
                        <FolderOutlined style={{ fontSize: '30px' }} />
                        <div>{element?.name || 'unknown'}</div>
                      </div>
                    )}
                    <DeleteOutlined
                      className={styles.deleteIcon}
                      onClick={() => removeFoldersAndFiles(element)}
                    />
                  </div>
                </div>
              );
            })
            .filter(Boolean)
        ) : (
          <TableView
            tableData={documentView.tableData}
            currentNode={currentNode}
            setCurrentNode={setCurrentNode}
            usersInDocuments={usersInDocuments}
            setUsersInDocuments={setUsersInDocuments}
            onExpand={onLoadData}
            columns={[
              ...(
                Documentcolumn(
                  t,
                  onLoadData,
                  user?.scopes?.includes(AppConstants.adminRole),
                  momentWithTimezone,
                  setCurrentNode,
                ) as any
              ).map((col: any) => {
                if (col.key === 'name') {
                  return { ...col, width: '40%' };
                }
                return col;
              }),
              {
                title: () => {
                  const allTableItems = documentView.tableData || [];

                  return (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                      }}
                    >
                      <Button
                        size="small"
                        onClick={async (e: any) => {
                          e.stopPropagation();
                          const allItems = allTableItems.filter(Boolean);
                          const fileIds: string[] = [];
                          const folderPromises: Promise<void>[] = [];

                          allItems.forEach((item: any) => {
                            const itemId = String(item?.id);
                            const isFile =
                              item.isLeaf === true ||
                              (item.type && item.type !== '');
                            if (isFile) {
                              fileIds.push(itemId);
                            } else {
                              folderPromises.push(selectFolder(itemId));
                            }
                          });

                          setSelectedFileIds(prev => [
                            ...new Set([...prev, ...fileIds]),
                          ]);
                          await Promise.all(folderPromises);
                        }}
                      >
                        {t('home.chat.attachments.folders.selectAll')}
                      </Button>
                      <Button
                        size="small"
                        onClick={async (e: any) => {
                          e.stopPropagation();
                          const currentItems = (
                            documentView.tableData || []
                          ).filter(Boolean);
                          const fileIdsToRemove = new Set<string>();
                          const folderPromises: Promise<void>[] = [];

                          currentItems.forEach((item: any) => {
                            const itemId = String(item.id);
                            const isFile =
                              item.isLeaf === true ||
                              (item.type && item.type !== '');
                            if (isFile) {
                              fileIdsToRemove.add(itemId);
                            } else {
                              folderPromises.push(deselectFolder(itemId));
                            }
                          });

                          setSelectedFileIds(prev =>
                            prev.filter(id => !fileIdsToRemove.has(id)),
                          );
                          await Promise.all(folderPromises);
                        }}
                      >
                        {t('home.chat.attachments.folders.removeAll')}
                      </Button>
                    </div>
                  );
                },
                key: 'action',
                width: '20%',
                align: 'center',
                render: (_, record) => {
                  const recordId = String(record.id);
                  const isFile =
                    record.isLeaf === true ||
                    (record.type && record.type !== '');
                  const isChecked = isFile
                    ? selectedFileIds.includes(recordId)
                    : selectedFolderIds.includes(recordId);

                  // Indeterminate: folder is not checked but has selected
                  // descendants (detected by the useEffect via backend).
                  const isIndeterminate =
                    !isFile && !isChecked && ancestorHintIds.has(recordId);

                  const status = record?.status || record?.extras?.status || '';
                  const isProcessing =
                    status === 'processing' ||
                    (isFile &&
                      (record.processed === false ||
                        record.processed === undefined));

                  return (
                    <div
                      onClick={(e: any) => e.stopPropagation()}
                      onClickCapture={(e: any) => {
                        if (isProcessing) {
                          e.stopPropagation();
                          e.preventDefault();
                          message.warning(
                            t(
                              'home.chat.attachments.files.uploadInProgressWarning',
                            ),
                          );
                        }
                      }}
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Tooltip
                        title={
                          isProcessing
                            ? t('home.documents.table.status.processing')
                            : ''
                        }
                      >
                        {isIndeterminate ? (
                          <Tooltip
                            title={t(
                              'home.chat.attachments.folders.partialSelection',
                              'Partial selection',
                            )}
                          >
                            <MinusCircleFilled
                              style={{
                                fontSize: 18,
                                color: '#faad14',
                                cursor: isProcessing
                                  ? 'not-allowed'
                                  : 'pointer',
                              }}
                              onClick={async (e: any) => {
                                e.stopPropagation();
                                if (isProcessing) return;
                                await selectFolder(recordId);
                                setCount(prev => prev + 1);
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Checkbox
                            checked={isChecked}
                            disabled={isProcessing}
                            onChange={async (e: CheckboxChangeEvent) => {
                              e.stopPropagation();
                              if (e.target.checked) {
                                if (isFile) {
                                  setSelectedFileIds(prev => [
                                    ...new Set([...prev, recordId]),
                                  ]);
                                } else {
                                  await selectFolder(recordId);
                                  setCount(prev => prev + 1);
                                }
                              } else {
                                if (isFile) {
                                  setSelectedFileIds(prev =>
                                    prev.filter(id => id !== recordId),
                                  );
                                } else {
                                  await deselectFolder(recordId);
                                  setCount(prev => prev + 1);
                                }
                              }
                            }}
                          />
                        )}
                      </Tooltip>
                    </div>
                  );
                },
              },
            ]}
            button={
              <Button
                type="primary"
                onClick={handleOk}
                style={{ borderRadius: '8px' }}
                loading={loading}
                disabled={loading}
              >
                <CheckOutlined />
                {t('home.chat.attachments.folders.confirm')}
              </Button>
            }
          />
        )}
      </Modal>
    </div>
  );
};

export default ModalAddFolders;
